import { useState, useEffect, useRef } from "react";
import { serverUrl } from "../../data/dataApi";

const Questionnaire = ({
    questions,
    originalQuery,
    onSubmit,
    onSkip,
    loading
}) => {
    const [answers, setAnswers] = useState(
        questions.map(() => ({ selectedIndex: null, customAnswer: '' }))
    );
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [direction, setDirection] = useState('next');
    const autoAdvanceTimeout = useRef(null);
    const previousAnswersRef = useRef(answers);

    const handleOptionSelect = (questionIndex, optionIndex, optionText) => {
        setAnswers(prev => {
            const newAnswers = [...prev];
            newAnswers[questionIndex] = {
                selectedIndex: optionIndex,
                customAnswer: ''
            };
            return newAnswers;
        });
    };

    const handleCustomInputChange = (questionIndex, value) => {
        setAnswers(prev => {
            const newAnswers = [...prev];
            newAnswers[questionIndex] = {
                selectedIndex: questions[questionIndex].options.length - 1,
                customAnswer: value
            };
            return newAnswers;
        });
    };

    const handleCustomInputSubmit = (e) => {
        e.preventDefault();
        
        // Clear any existing timeout
        if (autoAdvanceTimeout.current) {
            clearTimeout(autoAdvanceTimeout.current);
        }

        // Check if custom input has text
        const currentAnswer = answers[currentQuestion];
        if (currentAnswer.customAnswer.trim()) {
            if (currentQuestion < questions.length - 1) {
                // Move to next unanswered question
                handleNext();
            } else {
                // Last question - submit
                handleSubmit();
            }
        }
    };

    const handleSubmit = () => {
        const formattedAnswers = questions.map((q, i) => {
            const answer = answers[i];
            const isCustom = answer.selectedIndex === q.options.length - 1;

            return {
                question: q.question,
                answer: isCustom ? answer.customAnswer : q.options[answer.selectedIndex]
            };
        });

        onSubmit(originalQuery, formattedAnswers);
    };

    const handleNext = () => {
        if (currentQuestion < questions.length - 1) {
            setDirection('next');
            // Find the next unanswered question
            let nextQuestion = currentQuestion + 1;
            while (nextQuestion < questions.length) {
                const answer = answers[nextQuestion];
                const isAnswered = answer.selectedIndex !== null || answer.customAnswer.trim();
                if (!isAnswered) {
                    break;
                }
                nextQuestion++;
            }
            // If all subsequent questions are answered, just go to the next one
            if (nextQuestion >= questions.length) {
                nextQuestion = currentQuestion + 1;
            }
            setCurrentQuestion(nextQuestion);
        }
    };

    const handlePrev = () => {
        if (currentQuestion > 0) {
            setDirection('prev');
            setCurrentQuestion(prev => prev - 1);
        }
    };

    const allQuestionsAnswered = answers.every((answer, index) => {
        if (answer.selectedIndex === null && !answer.customAnswer.trim()) return false;
        const isCustom = answer.selectedIndex === questions[index].options.length - 1;
        if (isCustom && !answer.customAnswer.trim()) return false;
        return true;
    });

    // Auto-advance to next question or submit when answer is selected
    useEffect(() => {
        // Clear any existing timeout
        if (autoAdvanceTimeout.current) {
            clearTimeout(autoAdvanceTimeout.current);
        }

        // Check if the answer for the current question actually changed
        const currentAnswer = answers[currentQuestion];
        const previousAnswer = previousAnswersRef.current[currentQuestion];
        
        // Only check if selectedIndex changed (radio button selection)
        // Don't auto-advance for custom input text changes
        const radioButtonChanged = currentAnswer.selectedIndex !== previousAnswer.selectedIndex;
        
        // Update the ref for next comparison
        previousAnswersRef.current = answers;
        
        // Only auto-advance if a radio button was selected (not custom text input)
        if (radioButtonChanged && !loading && currentAnswer.selectedIndex !== null) {
            // Check if this is not the "Something else" option
            const isCustomOption = currentAnswer.selectedIndex === questions[currentQuestion].options.length - 1;
            
            if (!isCustomOption) {
                autoAdvanceTimeout.current = setTimeout(() => {
                    if (currentQuestion < questions.length - 1) {
                        // Move to next unanswered question
                        handleNext();
                    } else {
                        // Last question - submit
                        handleSubmit();
                    }
                }, 1000);
            }
        }

        // Cleanup timeout on unmount or when dependencies change
        return () => {
            if (autoAdvanceTimeout.current) {
                clearTimeout(autoAdvanceTimeout.current);
            }
        };
    }, [answers, currentQuestion, loading]);

    return (
        <div className="questionnaire-wrapper overflow-hidden tif-px-md-0 tif-px-15 tif-pt-md-35 tif-pt-20 tif-pb-md-45 tif-pb-25">
            <div className="questionnaire-container d-flex align-items-start tif-mx-auto">
                <img
                    src={`${serverUrl}/static/assets/gelest-chatbot-widget-logo.svg`}
                    className="message-user-img flex-shrink-0 d-md-block d-none"
                    alt="Gelest chatbot logo"
                />
                <div className="questionnaire-content flex-grow-1">
                    <p className="tif-mb-10">
                        To help me provide the most relevant information, please answer a few quick questions:
                    </p>

                    <div className="questionaire-steps d-flex tif-mb-10">
                        <button
                            className="pagination-btn"
                            disabled={currentQuestion === 0}
                            onClick={handlePrev}
                        >
                            <svg className="d-block" id="Group_1" data-name="Group 1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path id="Path_1" data-name="Path 1" d="M0,0H24V24H0Z" fill="none" />
                                <path id="Path_2" data-name="Path 2" d="M15,6,9,12l6,6" fill="none" stroke="#707070" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                            </svg>
                        </button>
                        <span className="tif-font-sm tif-color-oscar">{currentQuestion + 1} of {questions.length}</span>
                        <button
                            className="pagination-btn"
                            disabled={currentQuestion === questions.length - 1}
                            onClick={handleNext}
                        >
                            <svg className="d-block" id="Group_2" data-name="Group 2" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path id="Path_3" data-name="Path 3" d="M0,0H24V24H0Z" fill="none" />
                                <path id="Path_4" data-name="Path 4" d="M9,6l6,6L9,18" fill="none" stroke="#707070" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                            </svg>
                        </button>
                    </div>

                    <div className="questionnaire-questions">
                        <div key={questions[currentQuestion].id} className={`questionnaire-question tif-mb-25 fade-${direction}`}>
                            <p className="question-text fw-medium tif-mb-15">
                                {questions[currentQuestion].question}
                            </p>

                            <div className="question-options">
                                {questions[currentQuestion].options.slice(0, -1).map((option, oIndex) => (
                                    <label
                                        key={oIndex}
                                        className={`question-option d-flex align-items-center tif-py-10 tif-px-15 ${answers[currentQuestion].selectedIndex === oIndex ? 'selected' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name={`question-${currentQuestion}`}
                                            checked={answers[currentQuestion].selectedIndex === oIndex}
                                            onChange={() => handleOptionSelect(currentQuestion, oIndex, option)}
                                            className="d-none"
                                        />
                                        <svg className={`option-indicator ${answers[currentQuestion].selectedIndex === oIndex ? 'selected' : ''}`} id="Group_3" data-name="Group 3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                            <path id="Path_5" data-name="Path 5" d="M0,0H24V24H0Z" fill="none" />
                                            <path id="Path_6" data-name="Path 6" d="M5,12l5,5L20,7" fill="none" stroke="#24439c" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                        </svg>
                                        <span className="option-text tif-font-sm tif-ml-10">{option}</span>
                                    </label>
                                ))}

                                <form onSubmit={handleCustomInputSubmit}>
                                    <label
                                        className={`question-option d-flex align-items-center tif-py-10 tif-px-15 ${answers[currentQuestion].selectedIndex === questions[currentQuestion].options.length - 1 ? 'selected' : ''}`}>
                                        <svg className="something-else-icon" id="Group_4" data-name="Group 4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                            <path id="Path_7" data-name="Path 7" d="M0,0H24V24H0Z" fill="none" />
                                            <path id="Path_8" data-name="Path 8" d="M4,20H8L18.5,9.5a2.828,2.828,0,1,0-4-4L4,16v4" fill="none" stroke="#98a9b3" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                            <path id="Path_9" data-name="Path 9" d="M13.5,6.5l4,4" fill="none" stroke="#98a9b3" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                        </svg>
                                        <input
                                            type="text"
                                            className="custom-answer-input tif-py-0 tif-px-10"
                                            placeholder={questions[currentQuestion].options[questions[currentQuestion].options.length - 1]}
                                            value={answers[currentQuestion].customAnswer}
                                            onChange={(e) => handleCustomInputChange(currentQuestion, e.target.value)}
                                            maxLength={250}
                                        />
                                        <button
                                            type="submit"
                                            className={`${answers[currentQuestion].selectedIndex === questions[currentQuestion].options.length - 1 ? 'show' : ''}`}
                                            disabled={!answers[currentQuestion].customAnswer.trim()}
                                        >
                                            <svg id="Group_5" data-name="Group 5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                                <path id="Path_10" data-name="Path 10" d="M0,0H24V24H0Z" fill="none" />
                                                <path id="Path_11" data-name="Path 11" d="M5,12H19" fill="none" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                                <path id="Path_12" data-name="Path 12" d="M15,16l4-4" fill="none" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                                <path id="Path_13" data-name="Path 13" d="M15,8l4,4" fill="none" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                            </svg>
                                        </button>
                                    </label>
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="questionnaire-actions d-flex align-items-center justify-content-end gap-3 tif-mt-30">
                        <button
                            className="questionnaire-skip-btn"
                            onClick={() => onSkip(originalQuery)}
                            disabled={loading}
                        >
                            Skip & Continue
                            <svg id="Group_2" data-name="Group 2" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path id="Path_3" data-name="Path 3" d="M0,0H24V24H0Z" fill="none" />
                                <path id="Path_4" data-name="Path 4" d="M9,6l6,6L9,18" fill="none" stroke="#707070" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Questionnaire;
